import ColorFilter from '../../../Component/ColorFilter';
import Interactable from '../../../Component/Interactable';
import Renderable from '../../../Component/Renderable';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import { BoosterType } from '../../../State/Card/BoosterType';
import { CardActions } from '../../../State/Card/CardActions';
import CardStateHelper, {
    getGoldCardsCollected,
    getGoldCardsTotal,
} from '../../../State/Card/CardStateHelper';
import { GameActions } from '../../../State/Game/GameActions';
import { ContextType } from '../../../State/Game/IContext';
import { IGameState } from '../../../State/Game/IGameState';
import {
    LevelComponent,
    LevelStatus,
    WinCriteriaType,
} from '../../../State/Level/ILevelState';
import { LevelActions } from '../../../State/Level/LevelActions';
import { isEndGame } from '../../../State/Level/LevelStateHelper';
import { UIActions } from '../../../State/UI/UIActions';
import { noCoinsPopupID } from '../../../State/UI/UIStateHelper';
import {
    AdvertisementArea,
    AdvertisementType,
    RewardType,
} from '../../../Utility/SDK/Advertisement';
import { StartAnimation } from '../../Animation/AnimationHelper';
import { AUDIO_BOOSTER_JOKER, AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import { AudioTrackType } from '../../Audio/AudioClip';
import { StartAudio } from '../../Audio/AudioHelper';
import BaseUIHandler from '../BaseUIHandler';
import BaseUISystem from '../BaseUISystem';
import { applyDefaultButtonAnimation } from '../UIHelper';

const BTN_PLUS = 'top_container/coins/plus_button';
const BTN_SETTINGS = 'bottom_container/settings_button';
const BTN_END_GAME = 'bottom_container/end_game_button';

const FILL_STREAK = 'top_container/streak';
const FILL_SCORE = 'top_container/score';

/* prettier-ignore */ const PLUS_FIVE_GROUP = 'bottom_container/plus_five_group';
/* prettier-ignore */ const PLUS_FIVE_IMAGE_TEXT = PLUS_FIVE_GROUP + '/plus_five_image/plus_five_image_text';
/* prettier-ignore */ const PLUS_FIVE_BUTTON_TEXT = PLUS_FIVE_GROUP + '/plus_five_container/plus_five_button/plus_five_button_text';
/* prettier-ignore */ const PLUS_FIVE_BUTTON = PLUS_FIVE_GROUP + '/plus_five_container/plus_five_button';
/* prettier-ignore */ const PLUS_FIVE_AD_BUTTON = PLUS_FIVE_GROUP + '/plus_five_container/plus_five_ad_button';

const DISPLAY_COINS = 'top_container/coins';
const TEXT_COINS = 'top_container/coins/coins_text';

const JOKER_BUTTON_CONTAINER = 'bottom_container/joker_button_container';
const JOKER_BUTTON = `${JOKER_BUTTON_CONTAINER}/button`;
const JOKER_PRICE_TEXT = `${JOKER_BUTTON_CONTAINER}/price_label/price_text`;
const JOKER_PRICE_ICON = `${JOKER_PRICE_TEXT}/price_icon`;

const UNDO_BUTTON_CONTAINER = 'bottom_container/undo_button_container';
const UNDO_BUTTON = `${UNDO_BUTTON_CONTAINER}/button`;
const UNDO_PRICE_TEXT = `${UNDO_BUTTON_CONTAINER}/price_label/price_text`;
const UNDO_PRICE_ICON = `${UNDO_PRICE_TEXT}/price_icon`;

const BAR_GOLD_CARDS = 'bottom_container/golden_cards_bar';

export default class LevelHUDHandler extends BaseUIHandler {
    private fillStreak: GameObject;
    private fillScore: GameObject;

    private containerCoins: GameObject;
    private containerPlusFive: GameObject;
    private containerUndo: GameObject;
    private containerJoker: GameObject;
    private containerGoldCards: GameObject;

    private textCoins: GameObject;
    private textPlusFiveImage: GameObject;
    private textPlusFivePrice: GameObject;
    private textUndoPrice: GameObject;
    private textJokerPrice: GameObject;
    private textGoldCards: GameObject;

    private btnSettings: GameObject;
    private btnEndGame: GameObject;
    private btnPlus: GameObject;
    private btnUndo: GameObject;
    private btnJoker: GameObject;
    private btnPlusFive: GameObject;
    private btnPlusFiveAd: GameObject;

    private iconJokerPrice: GameObject;
    private iconUndoPrice: GameObject;

    private enabled: boolean = true;

    private interactables: Interactable[];

    constructor(uiSystem: BaseUISystem) {
        super('levelHUD', uiSystem);

        this.alwaysVisible = true;
    }

    public handleStateChanged(state: IGameState) {
        if (
            state.levelState.status !== LevelStatus.INTRO &&
            state.levelState.status !== LevelStatus.ACTIVE &&
            state.levelState.status !== LevelStatus.COMPLETE
        ) {
            this.enableInteractables(false);
            return;
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.BONUS_STREAK) !== -1) {
            this.setElementVisible(this.fillStreak, true);
            this.handleBonusStreak(state);
        } else {
            this.setElementVisible(this.fillStreak, false);
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.COINS_DISPLAY) !== -1) {
            this.setElementVisible(this.containerCoins, true);

            // Make sure the level state is not "Complete". This way the counter does not immediately
            // add the coins earned for completing the level. The coins display should only be updated
            // via the coins animation when returning to the world map.
            if (state.levelState.status !== LevelStatus.COMPLETE) {
                this.setCoinsText(state.playerState.coins);
            }
        } else {
            this.setElementVisible(this.containerCoins, false);
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.SETTINGS) !== -1) {
            this.setElementVisible(this.btnSettings, true);
        } else {
            this.setElementVisible(this.btnSettings, false);
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.BOOSTER_PLUS_FIVE) !== -1) {
            this.handleEndGamePlusFive(state);
        } else {
            this.setElementVisible(this.containerPlusFive, false);
            this.setElementVisible(this.btnEndGame, false);
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.BOOSTER_UNDO) !== -1) {
            this.setElementVisible(this.containerUndo, true);
            // Update undo booster price.
            this.setUndoPrice(state);
        } else {
            this.setElementVisible(this.containerUndo, false);
        }

        // prettier-ignore
        if (state.levelState.components.indexOf(LevelComponent.BOOSTER_JOKER) !== -1) {
            this.setElementVisible(this.containerJoker, true);
            // Update joker booster price.
            this.setJokerPrice(state);
        } else {
            this.setElementVisible(this.containerJoker, false);
        }

        // Only enable when the level is active.
        this.enableInteractables(
            state.levelState.status === LevelStatus.ACTIVE && this.enabled
        );

        // show golden cards progress bar in golden cards levels
        state.levelState.winCriteria.forEach(criteria => {
            if (criteria.type === WinCriteriaType.CLEAR_GOLD) {
                this.setElementVisible(this.containerGoldCards, true);
                this.handleGoldCards(state);
            }
        });
    }

    protected onCreate() {
        this.system.scene.on('UpdateScoreProgress', (total: boolean) => {
            const levelState = this.system.state.getState().levelState;
            const score = total
                ? levelState.score
                : levelState.score - levelState.bonus; // without bonus
            this.setScoreProgress(score, levelState.stars);
        });

        this.system.scene.on('ToggleLevelHUD', (toggle: boolean) => {
            this.enabled = toggle;
            this.enableInteractables(
                this.system.state.getState().levelState.status ===
                    LevelStatus.ACTIVE && this.enabled
            );
        });

        this.fillStreak = this.element.find(FILL_STREAK);
        this.fillScore = this.element.find(FILL_SCORE);

        this.containerCoins = this.element.find(DISPLAY_COINS);
        this.containerUndo = this.element.find(UNDO_BUTTON_CONTAINER);
        this.containerJoker = this.element.find(JOKER_BUTTON_CONTAINER);
        this.containerPlusFive = this.element.find(PLUS_FIVE_GROUP);

        this.textCoins = this.element.find(TEXT_COINS);
        this.textPlusFiveImage = this.element.find(PLUS_FIVE_IMAGE_TEXT);
        this.textPlusFivePrice = this.element.find(PLUS_FIVE_BUTTON_TEXT);
        this.textUndoPrice = this.element.find(UNDO_PRICE_TEXT);
        this.textJokerPrice = this.element.find(JOKER_PRICE_TEXT);

        this.btnSettings = this.element.find(BTN_SETTINGS);
        this.btnEndGame = this.element.find(BTN_END_GAME);
        this.btnPlus = this.element.find(BTN_PLUS);
        this.btnUndo = this.element.find(UNDO_BUTTON);
        this.btnJoker = this.element.find(JOKER_BUTTON);
        this.btnPlusFive = this.element.find(PLUS_FIVE_BUTTON);
        this.btnPlusFiveAd = this.element.find(PLUS_FIVE_AD_BUTTON);

        this.iconUndoPrice = this.element.find(UNDO_PRICE_ICON);
        this.iconJokerPrice = this.element.find(JOKER_PRICE_ICON);

        // Set initial coins text.
        this.setCoinsText(Game.state.getState().playerState.coins);

        // Button settings.
        this.btnSettings.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                UIActions.showPopup('settingsMenu', {
                    enableBtnQuit: true,
                })
            );
        });
        applyDefaultButtonAnimation(this.btnSettings, AUDIO_UI_BUTTON_DEFAULT);

        // Button plus (shop).
        this.btnPlus.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.showPopup('shopMenu'));
        });
        applyDefaultButtonAnimation(this.btnPlus, AUDIO_UI_BUTTON_DEFAULT);
        this.btnPlus.getComponent(
            Renderable
        ).visible = Game.state.getState().platform.IAPSupport;

        this.btnEndGame.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(LevelActions.levelEnd());
        });
        applyDefaultButtonAnimation(this.btnEndGame, AUDIO_UI_BUTTON_DEFAULT);

        // Undo button click.
        applyDefaultButtonAnimation(this.btnUndo);

        this.btnUndo.on(GameObjectEvent.SELECTED, () => {
            const state = this.system.state.getState();
            if (
                state.playerState.inventory.boosterUndo > 0 ||
                state.playerState.coins >= state.levelState.boosterPrices.undo
            ) {
                this.system.state.dispatch(
                    CardActions.playBooster(BoosterType.UNDO)
                );
            } else {
                this.system.state.dispatch(
                    UIActions.showPopup(noCoinsPopupID(state.levelState))
                );
            }
        });

        // Joker button.
        applyDefaultButtonAnimation(this.btnJoker);

        this.btnJoker.on(GameObjectEvent.SELECTED, () => {
            const state = this.system.state.getState();
            if (
                state.playerState.inventory.boosterJoker > 0 ||
                state.playerState.coins >= state.levelState.boosterPrices.joker
            ) {
                this.system.state.dispatch(
                    CardActions.playBooster(BoosterType.JOKER)
                );

                StartAudio(
                    this.system.scene,
                    AUDIO_BOOSTER_JOKER,
                    AudioTrackType.SFX
                );
            } else {
                this.system.state.dispatch(
                    UIActions.showPopup(noCoinsPopupID(state.levelState))
                );
            }
        });

        // 'plus-five' extra cards button.
        const plusFiveImageTextComponent = this.textPlusFiveImage.getComponent(
            UITextComponent
        );
        plusFiveImageTextComponent.text = plusFiveImageTextComponent.text
            .replace('[x]', '5')
            .toUpperCase();

        this.btnPlusFive.on(GameObjectEvent.SELECTED, () => {
            const state = this.system.state.getState();
            if (
                // prettier-ignore
                state.playerState.coins >= state.levelState.boosterPrices.plusFive
            ) {
                this.system.state.dispatch(
                    CardActions.playBooster(BoosterType.PLUS_FIVE)
                );
            } else {
                this.system.state.dispatch(
                    UIActions.showPopup(noCoinsPopupID(state.levelState))
                );
            }
        });

        applyDefaultButtonAnimation(this.btnPlusFive, AUDIO_UI_BUTTON_DEFAULT);

        this.btnPlusFiveAd.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(
                GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                    placement: {
                        id: 0 /*changed from Game.config.adRewardedID*/,
                        type: AdvertisementType.REWARDED,
                    },
                    payload: {
                        g_gameArea: AdvertisementArea.LEVEL_PLUS_FIVE_CARDS,
                        g_rewardType: RewardType.PLUS_FIVE_CARDS,
                    },
                })
            );
        });
        applyDefaultButtonAnimation(
            this.btnPlusFiveAd,
            AUDIO_UI_BUTTON_DEFAULT
        );

        // golden cards progress bar
        this.containerGoldCards = this.element.find(BAR_GOLD_CARDS);
        this.textGoldCards = this.containerCoins.find('counter');

        this.interactables = this.element.getComponentsInChildren(Interactable);
    }

    protected onDestroy(): void {
        this.system.scene.off('UpdateScoreProgress');
        this.system.scene.off('ToggleLevelHUD');
    }

    protected enableInteractables(value: boolean): void {
        this.interactables.forEach(interactable => {
            interactable.enabled = value;
        });

        this.setUndoButtonEnabled(
            value &&
                this.system.state.getState().levelState.cardsState.undoHistory
                    .length > 0
        );
    }

    private setElementVisible(element: GameObject, visible: boolean): void {
        element.getComponent(Renderable).visible = visible;
    }

    private handleEndGamePlusFive(state: IGameState): void {
        // 'plus-five' and game-over buttons.
        const plusFiveGroupRenderable = this.containerPlusFive.getComponent(
            Renderable
        );
        const btnEndGameRenderable = this.btnEndGame.getComponent(Renderable);

        // No cards left in stack but cards remain in play.
        if (isEndGame(state)) {
            if (plusFiveGroupRenderable.visible === false) {
                this.setPlusFivePrice(state);
                plusFiveGroupRenderable.visible = true;
                StartAnimation(this.element, 'ShowPlusFive', {
                    showAdButton:
                        state.levelState.boosterPrices.plusFive <= 200 &&
                        state.platform.adsRewarded,
                });
            }
            // No possible matches left.
            if (CardStateHelper.getCardMatches(state).length === 0) {
                if (btnEndGameRenderable.visible === false) {
                    btnEndGameRenderable.visible = true;
                    StartAnimation(this.btnEndGame, 'UIPopIn');
                }
            } else {
                btnEndGameRenderable.alpha = 0;
                btnEndGameRenderable.visible = false;
            }
        } else {
            plusFiveGroupRenderable.alpha = 0;
            plusFiveGroupRenderable.visible = false;
            btnEndGameRenderable.alpha = 0;
            btnEndGameRenderable.visible = false;
        }
    }

    private handleBonusStreak(state: IGameState): void {
        const prevLevelState = this.system.state.getPreviousState().levelState;

        // Set streak progress bar.
        const streakSequence: number =
            state.levelState.cardsState.streakSequence;
        const streakCards: number = state.levelState.cardsState.streakCards;
        if (streakSequence > prevLevelState.cardsState.streakSequence) {
            const perc: number = (streakSequence % 5) / 5;
            if (perc > 0) {
                this.setStreakProgress(perc, streakCards);
            } else {
                this.setStreakProgress(1, streakCards);
            }
        } else if (streakSequence === 0) {
            this.setStreakProgress(0, streakCards);
        }
    }

    private handleGoldCards(state: IGameState): void {
        const folded = getGoldCardsCollected(state.levelState.cardsState);

        const prevFolded = getGoldCardsCollected(
            state.previousState.levelState.cardsState
        );

        if (
            folded !== prevFolded ||
            state.levelState.status === LevelStatus.INTRO
        ) {
            this.setGoldCardsProgress(
                folded,
                getGoldCardsTotal(state.levelState.cardsState)
            );
        }
    }

    private setStreakProgress(progress: number, streakCards: number): void {
        StartAnimation(this.fillStreak, 'FillBarStreak', {
            progress: Math.min(progress, 1),
            cards: streakCards,
        });
    }

    private setScoreProgress(score: number, stars: number[]): void {
        StartAnimation(this.fillScore, 'FillBarScore', {
            scene: this.system.scene,
            score: score,
            stars: stars,
        });
    }

    private setGoldCardsProgress(folded: number, total: number) {
        StartAnimation(this.containerGoldCards, 'FillBarGoldCards', {
            system: this.system,
            folded: folded,
            total: total,
        });
    }

    private setUndoButtonEnabled(enabled: boolean) {
        this.btnUndo.getComponent(Interactable).enabled = enabled;

        if (enabled) {
            this.btnUndo.removeComponent(ColorFilter);
        } else {
            this.btnUndo.addComponent(ColorFilter);
            this.btnUndo.getComponent(ColorFilter).saturation = 0;
        }
    }

    private setCoinsText(coins: number) {
        this.textCoins.getComponent(UITextComponent).text = coins.toString();
    }

    private setPlusFivePrice(state: IGameState): void {
        const price = state.levelState.boosterPrices.plusFive;
        this.textPlusFivePrice.getComponent(
            UITextComponent
        ).text = price.toString();
    }

    private setUndoPrice(state: IGameState): void {
        const txtUndoPriceText = this.textUndoPrice.getComponent(
            UITextComponent
        );
        const coinUndoRenderable = this.iconUndoPrice.getComponent(Renderable);
        const items = state.playerState.inventory.boosterUndo;

        if (items > 0) {
            let free = Game.locale.getText('FREE');
            free = free.charAt(0).toUpperCase() + free.slice(1).toLowerCase();
            txtUndoPriceText.text = `${free} x${items}`;
            coinUndoRenderable.visible = false;
        } else {
            const price = state.levelState.boosterPrices.undo;
            txtUndoPriceText.text = price.toString();
            coinUndoRenderable.visible = true;
        }
    }

    private setJokerPrice(state: IGameState): void {
        const txtJokerPriceText = this.textJokerPrice.getComponent(
            UITextComponent
        );
        const coinJokerRenderable = this.iconJokerPrice.getComponent(
            Renderable
        );
        const items = state.playerState.inventory.boosterJoker;

        if (items > 0) {
            let free = Game.locale.getText('FREE');
            free = free.charAt(0).toUpperCase() + free.slice(1).toLowerCase();
            txtJokerPriceText.text = `${free} x${items}`;
            coinJokerRenderable.visible = false;
        } else {
            const price = state.levelState.boosterPrices.joker;
            txtJokerPriceText.text = price.toString();
            coinJokerRenderable.visible = true;
        }
    }
}
